import { KeyRound } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { useLogout } from '../hooks/use-auth';
import { ChangePasswordForm } from './change-password-form';

/**
 * Shown instead of the Admin while an administrator has forced a password
 * change (ADM-07). The backend refuses every other Admin call with
 * PASSWORD_CHANGE_REQUIRED until this is done; /admin/me then clears the flag.
 */
export function ForcedPasswordChangeScreen() {
  const logout = useLogout();

  return (
    <main className="bg-muted/30 flex min-h-svh items-center justify-center p-6">
      <div className="bg-card w-full max-w-sm space-y-5 rounded-lg border p-6 shadow-sm">
        <div className="space-y-1.5">
          <KeyRound className="text-primary size-5" aria-hidden />
          <h1 className="text-base font-semibold">Choose a new password</h1>
          <p className="text-muted-foreground text-xs">
            An administrator asked you to change your password before you continue.
          </p>
        </div>
        <ChangePasswordForm
          submitLabel="Save and continue"
          onDone={() => toast.success('Password changed.')}
        />
        <Button variant="link" size="sm" className="px-0 text-xs" onClick={logout}>
          Sign out instead
        </Button>
      </div>
    </main>
  );
}

/** Account-menu dialog for changing your own password. Mount only while open. */
export function ChangePasswordDialog({ onClose }: { onClose: () => void }) {
  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle className="text-base">Change password</DialogTitle>
          <DialogDescription className="text-xs">
            Your other sessions stay signed in.
          </DialogDescription>
        </DialogHeader>
        <ChangePasswordForm
          onCancel={onClose}
          onDone={() => {
            toast.success('Password changed.');
            onClose();
          }}
        />
      </DialogContent>
    </Dialog>
  );
}
